import { produce } from 'immer';
import { ComponentProps, ReactNode, useCallback, useRef } from 'react';
import { Image } from 'react-native';

import { ConversationProvider } from '@oui/app-core/src/components/ConversationContext';
import { usePersistedState } from '@oui/app-core/src/hooks/usePersistedState';
import { useTheme } from '@oui/app-core/src/styles';
import { StaticProvider } from '@oui/app-static/src/components/StaticContext';

export function WidgetProviders({ children }: { children?: ReactNode }) {
  const { theme } = useTheme();
  const [visitedPages, setVisitedPages] = usePersistedState('staticSessionVisitedPages', {});
  const visitedPagesRef = useRef(visitedPages);
  visitedPagesRef.current = visitedPages;

  const staticDelayFn: ComponentProps<typeof StaticProvider>['delayFn'] = useCallback(
    ({
      content,
      contentType,
      pageIndex,
    }: Parameters<NonNullable<ComponentProps<typeof StaticProvider>['delayFn']>>[0]) => {
      // track further page visited
      const previousPageIndex = visitedPagesRef.current[contentType] || 0;
      if (pageIndex > previousPageIndex) {
        setVisitedPages((curr) => {
          return produce(curr, (draft) => {
            draft[contentType] = Math.max(pageIndex, previousPageIndex);
          });
        });
      }
      // If the user has already made it past this screen, no delay needed
      if (pageIndex < previousPageIndex) return 0;

      const TESTING_SPEEDUP = global.e2e || process.env.STORYBOOK_CLIENT ? 3 : 1;
      // Video content is 60s delay, otherwise 40s
      if (content.includes('.mp4')) return 60000 / TESTING_SPEEDUP;
      return 40000 / TESTING_SPEEDUP;
    },
    [setVisitedPages],
  );

  return (
    <ConversationProvider
      botName="Rae"
      showTabsAfterCompletion
      sentMessageBackgroundColor={theme.color.accent100}
      botAvatar={
        <Image source={require('../../assets/Avatar.png')} style={{ width: 40, height: 40 }} />
      }
    >
      <StaticProvider delayFn={staticDelayFn}>{children}</StaticProvider>
    </ConversationProvider>
  );
}
